/**
 * A page whose code loads on the way in, and can be loaded ahead of the click.
 *
 * `React.lazy` suspends the first time it renders even when the chunk is already in the browser's cache, and
 * React 19 then holds the page back until 300 ms after its fallback appeared (`FALLBACK_THROTTLE_MS`). The
 * page's data reads start only after it commits, so every lazily loaded page paid that wait before asking for
 * anything. A page whose module is already loaded here renders directly instead, with no suspension at all:
 * boot (`route-prefetch.ts`), a hover on a link and the idle preload all load the module before the page is
 * drawn.
 *
 * The choice between the loaded component and the lazy one is made once per mount, so a page that did suspend
 * is never remounted (and its state lost) when its module arrives.
 */
import { lazy, useState, type ComponentType } from "react";

export interface LazyRoute<P extends object> {
  (props: P): React.JSX.Element;
  /** Loads the page's code; never rejects (a click asks again, and shows its own error). */
  preload: () => Promise<void>;
  /** Whether the code is in, so drawing the page will not suspend. */
  readonly loaded: boolean;
}

/** How many intent preloads are in flight: a stale chunk they meet must not reload the page (`main.tsx`). */
let quiet = 0;

/** Whether a failed chunk may belong to a preload nobody is waiting for. */
export function isQuietPreload(): boolean {
  return quiet > 0;
}

export function lazyRoute<M, P extends object = Record<string, never>>(
  load: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
): LazyRoute<P> {
  let component: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;

  const fetchModule = (): Promise<ComponentType<P>> => {
    pending ??= load().then(
      (module) => (component = pick(module)),
      (error: unknown) => {
        // Forget the failure, so the next attempt (the click) loads again rather than replaying it.
        pending = null;
        throw error;
      },
    );
    return pending;
  };

  const Lazy = lazy(() => fetchModule().then((page) => ({ default: page })));

  function Route(props: P) {
    const [Page] = useState<ComponentType<P>>(() => component ?? Lazy);
    return <Page {...props} />;
  }

  Route.preload = () => {
    if (component) return Promise.resolve();
    quiet += 1;
    return fetchModule().then(
      () => {
        quiet -= 1;
      },
      () => {
        quiet -= 1;
      },
    );
  };
  Object.defineProperty(Route, "loaded", { get: () => component !== null });
  return Route as LazyRoute<P>;
}
